import {
  Box,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
  useTheme,
} from '@material-ui/core'
import React from 'react'
import { Globe } from 'react-feather'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getSortedHeatingDemands } from '../../../../../demands/demand-util'
import {
  DemandFragment,
  DhwHeatingMode,
  HourSeriesFragment,
  TemperatureCurveFragment,
  TemperatureCurveType,
} from '../../../../../generated/graphql'
import { validators } from '../../../../../validation/validators'
import { ControlledSwitch } from '../../../../form/ControlledSwitch'
import { ControlledTextField } from '../../../../form/ControlledTextField'
import { FormGroup } from '../../../../form/FormGroup'
import { InternalFormValues } from '../../form-values'

type Props = {
  demands: DemandFragment[]
  temperatureCurves: TemperatureCurveFragment[]
  hourSeriesNameFormatter: (hourSeries: HourSeriesFragment) => string
}

export function HeatingOptions({
  demands,
  temperatureCurves,
  hourSeriesNameFormatter,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const theme = useTheme()
  const { control, errors } = useFormContext<InternalFormValues>()

  return (
    <Box flexGrow={1} display="flex" flexDirection="column">
      <FormGroup>
        <ControlledTextField
          control={control}
          name="parameters.hybridHeatPump.heatingTemperatureCurveId"
          rules={{ ...validators.required }}
          error={errors.parameters?.hybridHeatPump?.heatingTemperatureCurveId}
          label={t(
            'createProvider.forms.hybridHeatPump.heatingTemperatureCurveId',
          )}
          select
        >
          {temperatureCurves.map((curve) => (
            <MenuItem key={curve.id} value={curve.id}>
              {curve.type === TemperatureCurveType.ProjectSpecific ? (
                <ListItemText>{curve.name}</ListItemText>
              ) : (
                <Box display="flex" alignItems="center">
                  <ListItemIcon>
                    <Globe />
                  </ListItemIcon>
                  <ListItemText>{curve.name}</ListItemText>
                </Box>
              )}
            </MenuItem>
          ))}
        </ControlledTextField>
      </FormGroup>

      <FormGroup>
        <ControlledTextField
          control={control}
          name="parameters.hybridHeatPump.dhwHeatingMode"
          rules={{ ...validators.required }}
          error={errors.parameters?.hybridHeatPump?.dhwHeatingMode}
          label={t('createProvider.forms.hybridHeatPump.dhwHeatingMode')}
          select
        >
          {Object.values(DhwHeatingMode).map((mode) => (
            <MenuItem key={mode} value={mode}>
              {t(`dhwHeatingMode.${mode}`)}
            </MenuItem>
          ))}
        </ControlledTextField>
      </FormGroup>

      <Box mt={1} mb={2}>
        <Grid container>
          <Grid item xs={9}>
            <Typography>
              {t(
                'createProvider.forms.hybridHeatPump.heatCircuitTemperatureCurve',
              )}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Box display="flex" alignItems="center" justifyContent="center">
              <Typography>
                {t(
                  'createProvider.forms.hybridHeatPump.heatCircuitIsDhwCircuit',
                )}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
        {getSortedHeatingDemands(demands).map((demand, index) => (
          <Grid key={demand.id} container alignItems="center" spacing={2}>
            <Grid item xs={9}>
              <Box display="none">
                <ControlledTextField
                  control={control}
                  name={`parameters.hybridHeatPump.heatCircuits.${index}.demandId`}
                  defaultValue={demand.id}
                />
              </Box>
              <ControlledTextField
                control={control}
                name={`parameters.hybridHeatPump.heatCircuits.${index}.temperatureCurveId`}
                rules={{ ...validators.required }}
                error={
                  errors.parameters?.hybridHeatPump?.heatCircuits?.[index]
                    ?.temperatureCurveId
                }
                label={hourSeriesNameFormatter(demand.hourSeries)}
                select
              >
                {temperatureCurves.map((curve) => (
                  <MenuItem key={curve.id} value={curve.id}>
                    {curve.type === TemperatureCurveType.ProjectSpecific ? (
                      <ListItemText>{curve.name}</ListItemText>
                    ) : (
                      <Box display="flex" alignItems="center">
                        <ListItemIcon>
                          <Globe />
                        </ListItemIcon>
                        <ListItemText>{curve.name}</ListItemText>
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </ControlledTextField>
            </Grid>
            <Grid item xs={3}>
              <Box display="flex" alignItems="center" justifyContent="center">
                <ControlledSwitch
                  control={control}
                  name={`parameters.hybridHeatPump.heatCircuits.${index}.isDhwCircuit`}
                />
              </Box>
            </Grid>
          </Grid>
        ))}
      </Box>
    </Box>
  )
}
